import React, {
  useEffect, useMemo, useState, useCallback, ReactElement,
} from 'react';
import ChartGanttContainer from '../../../components/chart-gantt-container/chart-gantt-container.component';
import CheckAccess from '../../../components/check-access/check-access.component';
import NoContentBlock from '../../../components/no-content-block/no-content-block.component';
import ChartGantt from '../../../components/chart-gantt/chart-gantt.component';
import ChartGanttZoomBlock from '../../../components/chart-gantt-zoom-block/chart-gantt-zoom-block.component';
import StructureService from '../../../services/structure-service';
import FormatService from '../../../services/format-service';
import useCurrentUserPermissions from '../../../hooks/use-current-user-permissions';
import { DEFAULT_CHART_COLOR, GANTT_CHART_COLOR_LIST } from '../../../constants/system/colors';
import {
  DEAL_SCHEDULE_TASK_CREATE, DEAL_SCHEDULE_TASK_DELETE, DEAL_SCHEDULE_TASK_EDIT,
} from '../../../constants/entity/permissions';
import { TAB_INDEX_DEFAULT } from '../../../constants/system/tab-indexes';
import { DEAL_SCHEDULE_ZOOM_ITEM_LIST } from '../../../constants/entity/deal-schedule-zoom-items';
import { SETTING_NAME_TABLE_COLUMN_SETTINGS } from '../../../constants/entity/setting-names';
import { TABLE_NAME_DEAL_SCHEDULE_TASK_LIST } from '../../../constants/system/table-names';

const DealScheduleTasks: ReactMemoComponent = React.memo((props: ReactComponentProps): ReactElement => {
  const ID_PREFIX_CONTAINER_CHART_GANTT: string = 'container-chart-gantt';
  const DEFAULT_ZOOM: string = 'year';
  const ITEM_ID_PARENT_ROOT: number = 0;

  const {
    isEditMode,
    isNewDeal,
    tabIndex,
    dealId,
    dealScheduleIndex,
    dealScheduleData,
    dealScheduleZoom,
    tableColumnSettings,
    currentUser,
    onChange,
    onZoomChange,
    onTableColumnsChange,
  } = props;

  const permissionList: string[] = useCurrentUserPermissions();

  const dealScheduleTasksData: DealScheduleTask[] = dealScheduleData.tasks || [];
  const dealScheduleTaskLinksData: DealScheduleTaskLink[] = dealScheduleData.links || [];
  const dealScheduleId: number = dealScheduleData.id;

  const [chartData, setChartData]: [GanttChartData | null, Function] = useState<StateValueDefault>(null);
  const [chartZoom, setChartZoom]: [string | null, Function] = useState(DEFAULT_ZOOM);

  const dealScheduleTaskEditPermissions: string[] = useMemo((): string[] => [
    DEAL_SCHEDULE_TASK_EDIT, DEAL_SCHEDULE_TASK_DELETE,
  ], []);

  const chartInitDeps: DepsItem[] = useMemo((): DepsItem[] => [dealId, tabIndex], [dealId, tabIndex]);
  const tableVisibleColumns: string[] = useMemo((): string[] => (
    tableColumnSettings && tableColumnSettings.length
      ? tableColumnSettings.filter((tableField: TableField): boolean => !!tableField.isVisible)
        .map((tableField: TableField): string => tableField.name) : []
  ), [tableColumnSettings]);

  const chartContainerId: string = `${ID_PREFIX_CONTAINER_CHART_GANTT}-${dealScheduleId}`;
  const isCurrentTab: boolean = (tabIndex || tabIndex === TAB_INDEX_DEFAULT) && tabIndex === dealScheduleIndex;
  const withAddButton: boolean = StructureService.checkPermission(DEAL_SCHEDULE_TASK_CREATE, permissionList);

  const zoomChange: Function = useCallback((zoomValue: string): void => {
    if (onZoomChange) {
      onZoomChange(zoomValue);
    }
  }, [dealScheduleZoom]);

  const onDataChange: Function = useCallback((extractedData: JsonObject): void => {
    if (extractedData && isCurrentTab) {
      const dealScheduleTasks: DealScheduleTask[] = extractedData.data && extractedData.data.length
        ? extractedData.data.map((dealScheduleTask: GanttTask, dealScheduleTaskIndex: number): DealScheduleTask => ({
          id: FormatService.parseInt(dealScheduleTask.id as string),
          parentId: dealScheduleTask.parent && dealScheduleTask.parent === ITEM_ID_PARENT_ROOT.toString()
            ? FormatService.parseInt(dealScheduleTask.parent) : null,
          name: dealScheduleTask.text,
          startedAt: dealScheduleTask.start_date,
          assignedTo: dealScheduleTask.assigned_to,
          notes: dealScheduleTask.notes,
          color: dealScheduleTask.color,
          duration: dealScheduleTask.duration,
          progress: dealScheduleTask.progress,
          order: dealScheduleTaskIndex + 1,
        })) : [];

      const dealScheduleTaskLinks: DealScheduleTaskLink[] = extractedData.links && extractedData.links.length
        ? extractedData.links.map((dealScheduleTaskLink: GanttTaskLink): DealScheduleTaskLink => ({
          id: dealScheduleTaskLink.id,
          sourceId: FormatService.parseInt(dealScheduleTaskLink.source as string) as unknown as bigint,
          targetId: FormatService.parseInt(dealScheduleTaskLink.target as string) as unknown as bigint,
          type: dealScheduleTaskLink.type,
        })) : [];

      if (onChange) {
        onChange(dealScheduleId, dealScheduleTasks, dealScheduleTaskLinks);
      }
    }
  }, [dealScheduleId, isCurrentTab, tabIndex, dealScheduleIndex]);

  useEffect((): void => {
    setChartData({
      data: dealScheduleTasksData && dealScheduleTasksData.length
        ? dealScheduleTasksData.map((dealScheduleTaskItem: DealScheduleTask): GanttTask => ({
          id: dealScheduleTaskItem.id,
          parent: dealScheduleTaskItem.parentId ? dealScheduleTaskItem.parentId.toString()
            : ITEM_ID_PARENT_ROOT.toString(),
          text: dealScheduleTaskItem.name,
          start_date: dealScheduleTaskItem.startedAt,
          assigned_to: dealScheduleTaskItem.assignedTo,
          notes: dealScheduleTaskItem.notes,
          duration: dealScheduleTaskItem.duration,
          progress: dealScheduleTaskItem.progress,
          order: dealScheduleTaskItem.order ? dealScheduleTaskItem.order.toString() : null,
          color: dealScheduleTaskItem.color && GANTT_CHART_COLOR_LIST
            .find((colorItem: GanttChartColorItem): boolean => colorItem.name === dealScheduleTaskItem.color)
            ? dealScheduleTaskItem.color : DEFAULT_CHART_COLOR,
          open: true,
        })) : [],
      links: dealScheduleTaskLinksData && dealScheduleTaskLinksData.length
        ? dealScheduleTaskLinksData.map((dealScheduleTaskLinkItem: DealScheduleTaskLink): GanttTaskLink => ({
          id: dealScheduleTaskLinkItem.id,
          source: dealScheduleTaskLinkItem.sourceId as bigint,
          target: dealScheduleTaskLinkItem.targetId as bigint,
          type: dealScheduleTaskLinkItem.type,
        })) : [],
    });
  }, [dealScheduleTasksData, dealScheduleTaskLinksData, isEditMode, dealScheduleId]);

  useEffect((): void => {
    if (currentUser && !tableColumnSettings) {
      const tableColumnSettingsCommon: Setting = currentUser.settings
        && currentUser.settings.find((settingItem: Setting): boolean => (
          settingItem.name === SETTING_NAME_TABLE_COLUMN_SETTINGS
        ));

      if (onTableColumnsChange) {
        onTableColumnsChange(
          tableColumnSettingsCommon && tableColumnSettingsCommon.value
          && tableColumnSettingsCommon.value[TABLE_NAME_DEAL_SCHEDULE_TASK_LIST],
          true,
        );
      }
    }
  }, [currentUser]);

  useEffect((): void => {
    setChartZoom(
      dealScheduleZoom && DEAL_SCHEDULE_ZOOM_ITEM_LIST
        .map((dealScheduleZoomItem: DealScheduleZoomItem): string => dealScheduleZoomItem.name)
        .includes(dealScheduleZoom) ? dealScheduleZoom : DEFAULT_ZOOM,
    );
  }, [dealScheduleZoom]);

  return (
    <CheckAccess permissions={dealScheduleTaskEditPermissions} isEnabled={isEditMode || isNewDeal}>
      <ChartGanttContainer>
        {
          ((isEditMode || isNewDeal)
            || (dealScheduleTasksData && dealScheduleTasksData.length)
            || (dealScheduleTaskLinksData && dealScheduleTaskLinksData.length)) ? (
            <div>
              <ChartGanttZoomBlock
                zoomValue={chartZoom}
                zoomItemList={DEAL_SCHEDULE_ZOOM_ITEM_LIST}
                onZoomChange={zoomChange}
              />
              <ChartGantt
                isActive={isCurrentTab}
                isEditMode={isEditMode || isNewDeal}
                withAddButton={withAddButton}
                containerId={chartContainerId}
                chartZoom={chartZoom}
                chartData={chartData}
                chartInitDeps={chartInitDeps}
                tableVisibleColumns={tableVisibleColumns}
                onDataChange={onDataChange}
              />
            </div>
          ) : (
            <NoContentBlock>
              No schedule tasks
            </NoContentBlock>
          )
        }
      </ChartGanttContainer>
    </CheckAccess>
  );
});

export default DealScheduleTasks;
